import { createSelector } from 'reselect';

import { dataMainBoardFilteredByCurrentHall } from '../../domain/dataMainBoard/dataMainBoardFilteredByCurrentHall';
import { sortMainBoard } from '../../domain/dataMainBoard/sortMainBoard';
import {
  DataMainBoardResponse,
  DataMainBoardSearchParams,
} from '../../domain/depreciation/types';
import { LoadingState } from '../../domain/schemas';

import { RootState } from '../../store';
import {
  MainBoardSettingState,
  mainBoardCurrentHallNamesSelector,
  mainBoardOrderSelector,
  mainBoardSortSelector,
  mainBoardTableHeaderSubMenuMultiFilterSelector,
} from '../ui/mainBoardSetting';

/* --------------------------------------------------------------------------------------------------------------
 * Action Types
 ----------------------------------------------------------------------------------------------------------------*/

// API通信関連
const FETCH_DATA_MAIN_BOARD = 'FETCH_DATA_MAIN_BOARD' as const; // データ取得開始
const FETCH_DATA_MAIN_BOARD_REQUEST = 'FETCH_DATA_MAIN_BOARD_REQUEST' as const; // APIリクエスト中
const FETCH_DATA_MAIN_BOARD_SUCCESS = 'FETCH_DATA_MAIN_BOARD_SUCCESS' as const; // データ取得成功
// 画面を閉じる
const HIDE_DATA_MAIN_BOARD = 'HIDE_DATA_MAIN_BOARD' as const;
// 画面を更新する・完全リセット
const RENEW_DATA_MAIN_BOARD = 'RENEW_DATA_MAIN_BOARD' as const;

export const DataMainBoardActionTypes = {
  FETCH_DATA_MAIN_BOARD,
  FETCH_DATA_MAIN_BOARD_REQUEST,
  FETCH_DATA_MAIN_BOARD_SUCCESS,
  HIDE_DATA_MAIN_BOARD,
  RENEW_DATA_MAIN_BOARD,
};

/* --------------------------------------------------------------------------------------------------------------
 * Action Creators
 ----------------------------------------------------------------------------------------------------------------*/
/**
 * 基板番号別データを取得する
 * @param params 検索条件
 */
export function fetchDataMainBoardAction(params: DataMainBoardSearchParams) {
  return {
    type: FETCH_DATA_MAIN_BOARD,
    payload: { params },
  };
}

/**
 * 基板番号別データ取得開始時に実行
 */
function fetchDataMainBoardRequestAction() {
  return {
    type: FETCH_DATA_MAIN_BOARD_REQUEST,
  };
}

/**
 * 基板番号別データ取得成功時に実行
 * @param dataMainBoard 取得した基板番号別データ
 */
function fetchDataMainBoardSuccessAction(dataMainBoard: DataMainBoardResponse) {
  return {
    type: FETCH_DATA_MAIN_BOARD_SUCCESS,
    payload: {
      dataMainBoard,
    },
  };
}

/**
 * 基板番号別データを閉じる
 */
function hideDataMainBoardAction() {
  return {
    type: HIDE_DATA_MAIN_BOARD,
  };
}

/**
 * 基板番号別データを更新する
 */
function renewDataMainBoardAction() {
  return {
    type: RENEW_DATA_MAIN_BOARD,
  };
}

export const DataMainBoardActionCreators = {
  fetchDataMainBoardAction,
  fetchDataMainBoardRequestAction,
  fetchDataMainBoardSuccessAction,
  hideDataMainBoardAction,
  renewDataMainBoardAction,
};

/* --------------------------------------------------------------------------------------------------------------
 * Actions
 ----------------------------------------------------------------------------------------------------------------*/
export type FetchDataMainBoardAction = ReturnType<
  typeof fetchDataMainBoardAction
>;
type FetchDataMainBoardRequestAction = ReturnType<
  typeof fetchDataMainBoardRequestAction
>;
type FetchDataMainBoardSuccessAction = ReturnType<
  typeof fetchDataMainBoardSuccessAction
>;
type HideDataMainBoardAction = ReturnType<typeof hideDataMainBoardAction>;
type RenewDataMainBoardAction = ReturnType<typeof renewDataMainBoardAction>;

export type DataMainBoardAction =
  | FetchDataMainBoardAction
  | FetchDataMainBoardRequestAction
  | FetchDataMainBoardSuccessAction
  | HideDataMainBoardAction
  | RenewDataMainBoardAction;

/* --------------------------------------------------------------------------------------------------------------
 * State (状態管理)
 ----------------------------------------------------------------------------------------------------------------*/
type DataMainBoardState = {
  loadingState: LoadingState; // ローディング状態
  dataMainBoard: DataMainBoardResponse | undefined; // 基板番号別データ
};

export const initialState: DataMainBoardState = {
  loadingState: 'prepare',
  dataMainBoard: undefined,
};

/* --------------------------------------------------------------------------------------------------------------
 * Selector (状態取得)
 ----------------------------------------------------------------------------------------------------------------*/
/**
 * 基板番号別データをすべて取得する
 * @returns 基板番号別データ
 */
const dataMainBoardSelector = (state: RootState) =>
  state.dataMainBoard.dataMainBoard;

/**
 * 基板番号別データのローディング状態を取得する
 * @returns ローディング状態
 */
export const dataMainBoardLoadingStateSelector = (state: RootState) =>
  state.dataMainBoard.loadingState;

/**
 * 基板番号別データの現在の検索条件を取得する
 * @returns 検索条件
 */
export const dataMainBoardParamsSelector = (state: RootState) =>
  state.dataMainBoard.dataMainBoard?.setting;

/**
 * 基板番号別データのテーブルデータを取得
 * @returns テーブルデータ
 */
export const dataMainBoardTableDataSelector = createSelector(
  dataMainBoardSelector,
  (dataMainBoard) => {
    const tableData = dataMainBoard?.data;

    if (tableData === undefined)
      return {
        columns: [],
        rows: [],
      };

    return tableData;
  }
);

/**
 * 基板番号別データのデータが存在するか取得
 * @returns 存在するか（存在する場合true）
 */
export const dataMainBoardIsExistSelector = createSelector(
  dataMainBoardSelector,
  (dataMainBoard) => dataMainBoard?.data !== undefined
);

/**
 * 現在選択中の表示項目を取得する
 * @returns 選択中の表示項目
 */
export const dataMainBoardParamsFieldsSelector = createSelector(
  dataMainBoardParamsSelector,
  (params) => {
    const selectedFields = params?.fields;
    return selectedFields ? selectedFields : [];
  }
);

/**
 * マルチフィルターを適用する
 */
const applyMultiFiltering = (
  filteredRows: DataMainBoardResponse['data']['rows'],
  tableHeaderSubMenuMultiFilter: MainBoardSettingState['tableHeaderSubMenuMultiFilter'],
  columns: DataMainBoardResponse['data']['columns']
) => {
  // カラムコードとインデックスのマッピングを作成
  const columnIndexMap = new Map(
    columns.map((col, index) => [col.code, index])
  );

  // フィルタリングを適用
  return filteredRows.filter((row) => {
    for (const [columnCode, selectedValues] of Object.entries(
      tableHeaderSubMenuMultiFilter ?? {}
    )) {
      if (selectedValues.length === 0) continue;

      const colIndex = columnIndexMap.get(columnCode);
      if (colIndex === undefined) continue;

      const cellValue = row.data.at(colIndex)?.value;

      if (
        typeof cellValue !== 'string' ||
        !selectedValues.includes(cellValue)
      ) {
        return false;
      }
    }
    return true;
  });
};

/**
 * ソート処理されたテーブルデータを取得（フィルタリングも適用）
 * @returns ソート済み・フィルタリング済みテーブルデータ
 */
export const dataMainBoardSortedTableDataSelector = createSelector(
  [
    dataMainBoardSelector,
    mainBoardSortSelector,
    mainBoardOrderSelector,
    mainBoardCurrentHallNamesSelector,
    mainBoardTableHeaderSubMenuMultiFilterSelector,
  ],
  (
    dataMainBoard,
    sort,
    order,
    currentHallNamesFilter,
    tableHeaderSubMenuMultiFilter
  ) => {
    if (!dataMainBoard) {
      return {
        columns: [],
        rows: [],
      };
    }

    // 現設置店舗のフィルタが設定されている場合のみフィルタリング実行
    let data =
      currentHallNamesFilter.length > 0
        ? dataMainBoardFilteredByCurrentHall(
            dataMainBoard,
            currentHallNamesFilter
          ).data
        : dataMainBoard.data;

    // マルチフィルターが設定されているかどうかを確認
    const isTableHeaderSubMenuMultiFilter =
      tableHeaderSubMenuMultiFilter &&
      Object.values(tableHeaderSubMenuMultiFilter).some(
        (selected) => selected.length > 0
      );

    // マルチフィルターが設定されている場合はフィルタリングを適用
    if (isTableHeaderSubMenuMultiFilter && data.rows.length > 0) {
      data = {
        ...data,
        rows: applyMultiFiltering(
          data.rows,
          tableHeaderSubMenuMultiFilter,
          data.columns
        ),
      };
    }

    // その後ソート
    if (sort && order) {
      return sortMainBoard(data, order, sort);
    }
    return data;
  }
);

/* --------------------------------------------------------------------------------------------------------------
 * Reducer (状態更新ロジック)
 ----------------------------------------------------------------------------------------------------------------*/
export function dataMainBoardReducer(
  state = initialState,
  action: DataMainBoardAction
): DataMainBoardState {
  switch (action.type) {
    case FETCH_DATA_MAIN_BOARD_REQUEST:
      // APIリクエスト開始時、ローディング状態に変更
      return {
        ...state,
        loadingState: 'loading',
      };
    case FETCH_DATA_MAIN_BOARD_SUCCESS:
      // APIリクエスト成功時、ローディング状態をloadedに変更し、取得した基板番号別データを格納
      return {
        ...state,
        loadingState: 'loaded',
        dataMainBoard: action.payload.dataMainBoard,
      };
    case HIDE_DATA_MAIN_BOARD:
      // 画面を閉じる際、基板番号別データを初期化
      return {
        ...state,
        dataMainBoard: initialState.dataMainBoard,
      };
    case RENEW_DATA_MAIN_BOARD:
      // 画面を更新する・完全リセットする際、stateを初期化
      return initialState;
    default:
      // それ以外のアクションではstateを変更しない
      return state;
  }
}
